<!--
  =====================================================================
  SECTION SPLIT LINKS - Sezione principale homepage con due grandi link
  =====================================================================
  
  Due grandi aree link affiancate che si espandono all'80% on hover.
  Contengono il carosello news integrato. Banner estetico in fondo.
  
  PROPS:
  - content: Object - Dati sezione dal backend
  
  FEATURES:
  - Layout 80/20 fluido (transizione 1.0s)
  - News Carousel integrato dentro ogni sezione link
  - Banner estetico in fondo alla pagina
  - Ripristino totale logica e grafica pulsanti originali
  
  =====================================================================
-->

<template>
  <section class="section-split-links-wrapper" :class="{ 'mobile-intro-active': mobileIntroActive, 'mobile-content-visible': mobileContentVisible }">
    <div v-if="isMobile" class="mobile-layout">

      <header class="mobile-header">
        <img :src="content.leftLink.logoMobile || content.leftLink.logo" :alt="content.leftLink.alt" class="mobile-header__logo" />
      </header>

      <div class="mobile-news-area">
        <Transition name="mobile-spinner">
          <div v-if="isLoading" class="mobile-spinner-wrapper">

          </div>
        </Transition>
        <Transition name="mobile-news">
          <div v-if="showNews" class="mobile-news-grid">
            <a
              v-for="item in cassaMobileNews"
              :key="'cassa-' + item.slug"
              :href="getNewsLink(item.slug, 'left')"
              class="mobile-news-card"
              rel="noopener noreferrer"
            >
              <div class="mobile-news-card__img-wrap">
                <img :src="getFirstImageFromArray(item.image)" alt="" class="mobile-news-card__img" loading="lazy" />
              </div>
              <div class="mobile-news-card__body">
                <p class="mobile-news-card__title">{{ item.title }}</p>
              </div>
            </a>
            <a
              v-for="item in fondazioneMobileNews"
              :key="'fond-' + item.slug"
              :href="getNewsLink(item.slug, 'right')"
              class="mobile-news-card"
              rel="noopener noreferrer"
            >
              <div class="mobile-news-card__img-wrap">
                <img :src="getFirstImageFromArray(item.image)" alt="" class="mobile-news-card__img" loading="lazy" />
              </div>
              <div class="mobile-news-card__body">
                <p class="mobile-news-card__title">{{ item.title }}</p>
              </div>
            </a>
          </div>
        </Transition>
      </div>

      <div class="mobile-bottom">
        <img
          :src="isLandscape && content.horizontalCastleImage
            ? content.horizontalCastleImage
            : (content.rightCarouselImageMobile || content.rightCarouselImage)"
          alt=""
          class="mobile-bottom__castle"
        />
        <div class="mobile-bottom__cta-group">
          <a
            :href="content.leftLink.url"
            class="mobile-bottom__cta mobile-bottom__cta--cassa"
            :class="{ 'mobile-bottom__cta--loading': isLoading }"
            rel="noopener noreferrer"
          ><span class="mobile-bottom__cta-label">{{ content.leftLink.textShort }}</span></a>
          <a
            :href="content.rightLink.url"
            class="mobile-bottom__cta mobile-bottom__cta--fondazione"
            :class="{ 'mobile-bottom__cta--loading': isLoading }"
            rel="noopener noreferrer"
          ><span class="mobile-bottom__cta-label">{{ content.rightLink.textShort }}</span></a>
          <div class="mobile-bottom__footer-strip">
            <a
              v-for="l in cassaFooterLinks"
              :key="l.label"
              :href="l.href"
              class="mobile-bottom__footer-link"
              rel="noopener noreferrer"
            >{{ l.label }}</a>
          </div>
        </div>
      </div>

    </div>

    <div v-if="!isMobile" class="section-split-links">

      <div
        class="link-panel link-panel--left"
        :class="{ 'is-hovered': isLeftHovered && !isMobile, 'is-dimmed': isRightHovered && !isMobile, 'is-mobile-active': isMobile }"
        @mouseenter="handleHover('left')"
        @mousemove="handleMove('left')"
        @mouseleave="handleLeave"
        @transitionend="onPanelTransitionEnd"
        @focusin="handleHover('left')"
        @focusout="handleFocusOut"
      >
        <div class="link-panel__content">
          <a 
            :href="content.leftLink.url"
            class="link-panel__main-info"
            rel="noopener noreferrer"
            @click.capture="preventUnlessExpanded('left', $event)"
          >
            <img
              :src="content.leftLink.logo"
              alt=""
              class="link-panel__logo"
            />

            <h2 class="link-panel__title link-panel__title--hover">
              <span v-html="(isLeftHovered || isMobile) ? content.leftLink.textLong : content.leftLink.textShort"></span>
            </h2>
          </a>

          <div class="news-integrated news-integrated--left" v-if="!isMobile && content.leftNewsCarousel?.items?.length > 0">
            <h2 class="news-section-title">notizie in evidenza</h2>
            <div class="news-carousel-relative-wrapper">
              <div
                class="carousel-viewport"
                ref="viewportLeft"
                :class="{ 'is-dragging': isDraggingLeft }"
                @pointerdown="onPointerDownLeft"
                @pointermove="onPointerMoveLeft"
                @pointerup="onPointerUpLeft"
                @pointercancel="onPointerUpLeft"
              >
                <div class="carousel-track" ref="trackLeft" @dragstart.prevent>
                  <div v-for="item in content.leftNewsCarousel.items" :key="item.slug" class="news-card">
                    <a
                      :href="getNewsLink(item.slug, 'left')"
                      class="news-card-link"
                      draggable="false"
                    >
                      <div class="news-image-wrapper">
                        <img :src="getFirstImageFromArray(item.image)" alt="" class="news-image" draggable="false" />
                      </div>
                      <div class="news-content">
                        <h3 class="news-title">{{ item.title }}</h3>
                      </div>
                    </a>
                  </div>
                </div>
              </div>

              <button v-show="canPrevLeft" class="arrow arrow-left" @click.stop="scrollPrevLeft()" aria-label="Scorri a sinistra">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <button v-show="canNextLeft" class="arrow arrow-right" @click.stop="scrollNextLeft()" aria-label="Scorri a destra">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>

            <div class="dots-navigation" v-if="totalDotsLeft > 1">
              <button
                v-for="(item, index) in totalDotsLeft"
                :key="index"
                class="dot"
                :class="{ active: currentDotIndexLeft === index }"
                @click.stop="goToDotLeft(index)"
                :aria-label="`Vai al gruppo ${index + 1}`"
              />
            </div>
          </div>

          <div class="link-panel__buttons" v-if="!isMobile">
            <a
              v-for="(button, btnIdx) in content.leftLink.buttons"
              :key="button.id"
              :href="resolvePlatformLink(button)"
              class="bottom-action bottom-action--banking"
              :class="btnIdx === 0 ? 'bottom-action--banking-left' : 'bottom-action--banking-right'"
              :target="button.target || '_blank'"
              rel="noopener noreferrer"
              :aria-label="button.ariaLabel"
              @click.capture="preventUnlessExpanded('left', $event)"
            >
              <div class="vertical-bar"></div>
              <img
                v-if="button.image"
                :src="button.image"
                alt=""
                class="bottom-action__logo"
              />
              <div class="bottom-action__text">
                <span class="bottom-action__text-primary">{{ getButtonPrimaryText(button) }}</span>
                <span class="bottom-action__text-secondary">{{ getButtonSecondaryText(button) }}</span>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div class="separator"></div>

      <div
        class="link-panel link-panel--right"
        :class="{ 'is-hovered': isRightHovered && !isMobile, 'is-dimmed': isLeftHovered && !isMobile, 'is-mobile-active': isMobile }"
        @mouseenter="handleHover('right')"
        @mousemove="handleMove('right')"
        @mouseleave="handleLeave"
        @transitionend="onPanelTransitionEnd"
        @focusin="handleHover('right')"
        @focusout="handleFocusOut"
      >
        <div class="link-panel__content">
          <a 
            :href="content.rightLink.url"
            class="link-panel__main-info"
            rel="noopener noreferrer"
            @click.capture="preventUnlessExpanded('right', $event)"
          >
            <img
              :src="content.rightLink.logo"
              alt=""
              class="link-panel__logo"
            />
            <h2 class="link-panel__title link-panel__title--hover">
              <span v-html="(isRightHovered || isMobile) ? content.rightLink.textLong : content.rightLink.textShort"></span>
            </h2>
          </a>

          <div class="news-integrated news-integrated--right" v-if="!isMobile && content.rightNewsCarousel?.items?.length > 0">
            <h2 class="news-section-title">notizie in evidenza</h2>
            <div class="news-carousel-relative-wrapper">
              <div
                class="carousel-viewport"
                ref="viewportRight"
                :class="{ 'is-dragging': isDraggingRight }"
                @pointerdown="onPointerDownRight"
                @pointermove="onPointerMoveRight"
                @pointerup="onPointerUpRight"
                @pointercancel="onPointerUpRight"
              >
                <div class="carousel-track" ref="trackRight" @dragstart.prevent>
                  <div v-for="item in content.rightNewsCarousel.items" :key="item.slug" class="news-card">
                    <a
                      :href="getNewsLink(item.slug, 'right')"
                      class="news-card-link"
                      draggable="false"
                    >
                      <div class="news-image-wrapper">
                        <img :src="getFirstImageFromArray(item.image)" alt="" class="news-image" draggable="false" />
                      </div>
                      <div class="news-content">
                        <h3 class="news-title">{{ item.title }}</h3>
                      </div>
                    </a>
                  </div>
                </div>
              </div>

              <button v-show="canPrevRight" class="arrow arrow-left" @click.stop="scrollPrevRight()" aria-label="Scorri a sinistra">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <button v-show="canNextRight" class="arrow arrow-right" @click.stop="scrollNextRight()" aria-label="Scorri a destra">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>

            <div class="dots-navigation" v-if="totalDotsRight > 1">
              <button
                v-for="(item, index) in totalDotsRight"
                :key="index"
                class="dot"
                :class="{ active: currentDotIndexRight === index }"
                @click.stop="goToDotRight(index)"
                :aria-label="`Vai al gruppo ${index + 1}`"
              />
            </div>
          </div>

          <div class="link-panel__buttons" v-if="!isMobile">
            <template v-if="content.rightLink.buttons?.length > 0">
              <a
                v-for="(button, btnIdx) in content.rightLink.buttons"
                :key="button.id"
                :href="resolvePlatformLink(button)"
                class="bottom-action bottom-action--banking"
                :class="btnIdx === 0 ? 'bottom-action--banking-left' : 'bottom-action--banking-right'"
                :target="button.target || '_blank'"
                rel="noopener noreferrer"
                :aria-label="button.ariaLabel"
                @click.capture="preventUnlessExpanded('right', $event)"
              >
                <div class="vertical-bar"></div>
                <img
                  v-if="button.image"
                  :src="button.image"
                  alt=""
                  class="bottom-action__logo"
                />
                <div class="bottom-action__text">
                  <span class="bottom-action__text-primary">{{ getButtonPrimaryText(button) }}</span>
                  <span class="bottom-action__text-secondary">{{ getButtonSecondaryText(button) }}</span>
                </div>
              </a>
            </template>
            <div v-else class="bottom-action bottom-action--banking bottom-action--placeholder" aria-hidden="true"></div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="center-image-wrapper"
      v-if="!isMobile && content.centerImage"
      :class="{ 'at-left': isLeftHovered, 'at-right': isRightHovered }"
      @mouseenter="handleLeave"
      @click="resetHover"
    >
      <img :src="content.centerImage" alt="" class="center-image" />
    </div>

  </section>
</template>

<script setup>
// ───────────────────────────────────────────────────────────────────
// IMPORTS
// ───────────────────────────────────────────────────────────────────

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useCarouselScroll } from '@/composables/useCarouselScroll.js'
import { state, setHoverSection, setHoverActive } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// PROPS
// ───────────────────────────────────────────────────────────────────

const props = defineProps({
  content: { type: Object, required: true }
})

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE
// ───────────────────────────────────────────────────────────────────

// Stato hover derivato dallo store: la sezione resta "aperta" finché hoverActive
// è true sul suo lato — quindi anche quando il mouse è sui link in alto (che
// mantengono hoverActive), non solo quando è sul pannello.
const isLeftHovered = computed(() => state.hoverActive && state.hoverSection === 'left')
const isRightHovered = computed(() => state.hoverActive && state.hoverSection === 'right')
const isMobile = ref(false)
const isLandscape = ref(false)

// Hover disabilitato per il primo secondo dopo il load: evita che la sezione si apra
// da sola se al caricamento il mouse è già fermo dentro un pannello.
const hoverReady = ref(false)

// Mobile loading animation (spinner for 2s on first load)
const isLoading = ref(true)
const showNews = ref(false)

// Mobile intro animation
const mobileIntroActive = ref(typeof window !== 'undefined' && window.innerWidth <= 768)
// Panels and content hidden until after intro sequence completes
const mobileContentVisible = ref(typeof window !== 'undefined' && window.innerWidth > 768)

// Caroselli news (sinistro = cassa, destro = fondazione).
// Lo scorrimento e' lo scroll NATIVO del browser: qui restano solo frecce,
// puntini e il trascinamento col mouse (che non tocca il dito).
const {
  viewport: viewportLeft, track: trackLeft, isDragging: isDraggingLeft,
  totalDots: totalDotsLeft, currentDotIndex: currentDotIndexLeft,
  canScrollLeft: canPrevLeft, canScrollRight: canNextLeft,
  scrollPrev: scrollPrevLeft, scrollNext: scrollNextLeft, goToDot: goToDotLeft,
  onPointerDown: onPointerDownLeft, onPointerMove: onPointerMoveLeft, onPointerUp: onPointerUpLeft,
  measure: measureLeft
} = useCarouselScroll({
  itemCount: () => props.content.leftNewsCarousel?.items?.length || 0,
  cardSelector: '.news-card',
  observeResize: false
})

const {
  viewport: viewportRight, track: trackRight, isDragging: isDraggingRight,
  totalDots: totalDotsRight, currentDotIndex: currentDotIndexRight,
  canScrollLeft: canPrevRight, canScrollRight: canNextRight,
  scrollPrev: scrollPrevRight, scrollNext: scrollNextRight, goToDot: goToDotRight,
  onPointerDown: onPointerDownRight, onPointerMove: onPointerMoveRight, onPointerUp: onPointerUpRight,
  measure: measureRight
} = useCarouselScroll({
  itemCount: () => props.content.rightNewsCarousel?.items?.length || 0,
  cardSelector: '.news-card',
  observeResize: false
})


// Prime 2 notizie per il layout mobile (grid 2×2)
const cassaMobileNews = computed(() => props.content.leftNewsCarousel?.items?.slice(0, 2) || [])
const fondazioneMobileNews = computed(() => props.content.rightNewsCarousel?.items?.slice(0, 2) || [])

// Link footer (Privacy/Contatti) della sola CASSA, mostrati sulla barra verde in fondo su mobile.
// Dati dal json (content.leftLink.footerLinks); href = url assoluto se presente, altrimenti base+path.
const cassaFooterLinks = computed(() => {
  const link = props.content?.leftLink
  if (!link) return []
  const base = (link.url || '').replace(/\/$/, '')
  return (link.footerLinks || []).map(it => ({ label: it.label, href: it.url || base + it.path }))
})

// ───────────────────────────────────────────────────────────────────
// METHODS
// ───────────────────────────────────────────────────────────────────

function handleHover(section) {
  if (isMobile.value) return
  if (!hoverReady.value) return
  setHoverSection(section)
  setHoverActive(true)
  // isLeftHovered/isRightHovered ora sono computed dallo store: niente assegnazioni
  // (la rimisura del carosello, quando il pannello si apre, la fa da sola
  //  useCarouselScroll osservando il track)
}

// L'ingresso nel pannello (mouseenter) arriva UNA volta sola. Se il mouse e' gia'
// sopra una meta' al caricamento, quell'ingresso arriva durante il secondo di
// attesa (hoverReady falso) e viene scartato; muovendo il mouse nella stessa meta',
// compresi loghi e scritte, non ne arrivano altri e la meta' restava chiusa.
// Il movimento ripara: appena l'attesa e' finita apre la meta' sotto il mouse.
// A mouse fermo non apre niente, quindi l'attesa continua a servire.
// (2026-09-16, segnalazione del 24/07, misurato su Chrome)
function handleMove(section) {
  if (state.hoverActive && state.hoverSection === section) return
  handleHover(section)
}

// Accessibilita' (call 02/09, lettori di schermo): i link di una meta' chiusa sono
// trasparenti ma raggiungibili con Tab. Senza questo chi naviga da tastiera arrivava
// su link invisibili; ora la meta' si apre quando il fuoco ci entra e si chiude
// quando esce (non quando passa fra due link della stessa meta'). (2026-09-16)
function handleFocusOut(event) {
  if (event.currentTarget.contains(event.relatedTarget)) return
  handleLeave()
}

// I caroselli non si rimisurano durante le animazioni (observeResize: false): la
// larghezza cambia a ogni fotogramma per 1s, e rimisurare e ridisegnare i puntini
// a ogni fotogramma rendeva l'apertura dei pannelli a scatti (2026-09-17, confronto
// a occhio di Teo fra le versioni). Si rimisura una volta, quando finisce
// un'animazione che cambia la larghezza del carosello: il pannello (flex-grow,
// 50 -> 80/20), il blocco notizie (padding in vw, animato anche al resize della
// finestra), le card (flex-basis ai breakpoint). Le altre (opacita', colore,
// transform) non la cambiano e non rimisurano.
const PROPRIETA_LARGHEZZA = new Set(['flex-grow', 'flex-basis', 'padding-left', 'padding-right', 'width'])
function onPanelTransitionEnd(event) {
  if (!PROPRIETA_LARGHEZZA.has(event.propertyName)) return
  measureLeft()
  measureRight()
}

function preventUnlessExpanded() {}

function handleLeave() {
  if (isMobile.value) return
  resetHover()
}

function resetHover() {
  // l'espansione segue lo store: basta spegnere hoverActive (con il piccolo
  // ritardo nel setter, che fa da ponte verso i link in alto)
  setHoverActive(false)
}

function getNewsLink(slug, side) {
  return `/${side === 'left' ? 'cassa' : 'fondazione'}/news/${slug}`
}

function getFirstImageFromArray(img) {
  return Array.isArray(img) ? img[0] : img
}

function getButtonPrimaryText(button) {
  if (button.textPrimary) return button.textPrimary
  if (button.ariaLabel) return button.ariaLabel.split(' ')[0]
  return ''
}

function getButtonSecondaryText(button) {
  if (button.textSecondary) return button.textSecondary
  if (button.ariaLabel) return button.ariaLabel.split(' ').slice(1).join(' ')
  return ''
}

// ───────────────────────────────────────────────────────────────────
// PLATFORM & LINK RESOLUTION
// ───────────────────────────────────────────────────────────────────

/**
 * Detects current platform with high precision
 * 
 * @returns {String} 'ios' | 'android' | 'desktop'
 */
function getPlatform() {
  const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
  
  // 1. Check UserAgentData (Modern browsers)
  if (navigator.userAgentData && navigator.userAgentData.platform) {
    const p = navigator.userAgentData.platform.toLowerCase();
    if (p.includes('android')) return 'android';
    if (p.includes('ios') || p.includes('iphone') || p.includes('ipad')) return 'ios';
  }

  // 2. Precise iOS detection (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  
  // 3. Android detection
  if (/android/.test(ua)) return 'android';

  // 4. iPadOS 13+ special case (presents as Macintosh with touch)
  if (ua.includes('macintosh') && navigator.maxTouchPoints > 1) return 'ios';

  return 'desktop';
}

/**
 * Resolves platform-specific link for CRFOnline action
 * Supports new 'platforms' object and legacy 'linkMobile/linkDesktop' fields
 * 
 * @param {Object} icon - Action icon object
 * @returns {String} Resolved link URL
 */
function resolvePlatformLink(icon) {
  const platform = getPlatform();
  const desktopFallback = icon.linkDesktop || icon.link || '#';

  // Strategy A: New structured 'platforms' object
  if (icon.platforms) {
    return icon.platforms[platform] || icon.platforms.desktop || desktopFallback;
  }

  // Strategy B: Legacy flat keys with backward compatibility
  const androidLink = icon.linkAndroid || icon.linkMobile || icon.link || desktopFallback;
  const iosLink = icon.linkIOS || icon.link || desktopFallback;

  if (platform === 'ios') return iosLink || desktopFallback;
  if (platform === 'android') return androidLink || desktopFallback;
  return desktopFallback;
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

onMounted(async () => {
  isMobile.value = window.innerWidth <= 1024
  isLandscape.value = window.innerWidth <= 1024 && window.innerWidth > window.innerHeight
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 1024
    isLandscape.value = window.innerWidth <= 1024 && window.innerWidth > window.innerHeight
  })

  // Abilita gli hover solo dopo 1s dal load (vedi hoverReady sopra).
  setTimeout(() => { hoverReady.value = true }, 1000)

  await nextTick()

  // Mobile intro sequence:
  // T+0ms   → arnia grande + loading dots (stato iniziale già impostato via ref)
  // T+1100ms → arnia inizia a rimpicciolirsi verso la posizione finale
  // T+1700ms → pannelli, loghi e titoli appaiono con animazione
  if (isMobile.value) {
    setTimeout(() => {
      isLoading.value = false
      // Nasconde anche il loading screen globale di index.html
      if (typeof window.hideLoadingScreen === 'function') window.hideLoadingScreen()
      // news entrano nel DOM solo dopo che lo spinner è sparito (0.3s leave)
      setTimeout(() => { showNews.value = true }, 350)
    }, 1000)
  }

  if (mobileIntroActive.value) {
    setTimeout(() => {
      mobileIntroActive.value = false
      setTimeout(() => {
        mobileContentVisible.value = true
      }, 600)
    }, 1100)
  }
})

onUnmounted(() => {})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/mixins";

.section-split-links-wrapper {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: $clr-bg-page;
  overflow: hidden;
}

.section-split-links {
  display: flex;
  height: 100%;
  width: 100%;

  @include respond-below(md) {
    flex-direction: column;
    height: 100vh; // Altezza fissa per gestire espansione pannelli
    overflow: hidden;
  }
}

.link-panel {
  flex: 50;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: flex 1.0s cubic-bezier(0.4, 0, 0.2, 1), height 1.0s cubic-bezier(0.4, 0, 0.2, 1);
  background: $clr-bg-page;

  &.is-hovered {
    flex: 80;

    @include respond-below(md) {
      flex: none;
      height: 78vh !important;

      .link-panel__content {
        justify-content: flex-start; // Forza l'inizio dall'alto per evitare tagli in basso
        padding-top: $space-1;
        padding-bottom: $space-1;
        gap: $space-0-5; // Ridotto per compattare
      }

      .link-panel__main-info {
        gap: $space-0-5; // Meno spazio tra logo, titolo e bottoni
      }

      .link-panel__logo {
        height: 50px; // Leggermente più piccolo
      }

      .link-panel__title {
        font-size: 1.3rem;
        margin-top: $space-0-5;
        margin-bottom: $space-0-5;
      }
    }
  }

  &.is-dimmed {
    flex: 20;

    @include respond-below(md) {
      flex: none;
      height: 10vh !important; // Striscia più sottile per la traccia

      .link-panel__content {
        padding-top: 0;
        padding-bottom: 0;
        height: 100%;
        justify-content: center;
      }

      .link-panel__main-info {
        transform: none !important; // Rimuove ogni traslazione diagonale
        margin: 0;
        gap: 0;
        display: flex;
        // align-items e justify-content sono ora gestiti dai modificatori --left/--right
      }

      .link-panel__buttons,
      .news-integrated {
        display: none !important; // Forza la sparizione per evitare overflow e problemi di centering
      }
    }
  }

  @include respond-below(md) {
    flex: 1;
    width: 100%;
    min-height: 0;
  }

  &.link-panel--left.is-mobile-active,
  &.link-panel--right.is-mobile-active {
    @include respond-below(md) {
      flex: 1;
    }
  }

  &.is-mobile-active {
    @include respond-below(md) {
      flex: 1;

      .link-panel__content {
        pointer-events: auto !important;
        justify-content: center;
        padding: $space-1 $space-0-5;
        gap: $space-0-5;
      }

      .link-panel__main-info {
        transform: none;
        pointer-events: auto;
        padding: $space-0-5 0;
        gap: $space-0-5;
      }

      .link-panel__logo {
        display: block;
        height: 58px;
      }

      .link-panel__title {
        font-size: clamp(1.3rem, 5.2vw, 1.65rem);
        margin-top: 0;
      }

      .link-panel__buttons {
        display: flex !important;
        opacity: 1;
        pointer-events: auto;
        flex-direction: column;
        align-items: center;
        gap: $space-0-5;
        width: 100%;
        margin-top: $space-0-5;
      }
    }
  }
}



.link-panel__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 3vh $space-2 100px;
  position: relative;
  gap: 3vh;
  pointer-events: none !important;

  .is-hovered & {
    pointer-events: auto !important;
    gap: 1.5vh;
  }

  @include respond-below(md) {
    padding: $space-0-5;
    gap: $space-0-25;
    justify-content: center;
  }
}

.link-panel__main-info {
  width: fit-content;
  max-width: 90%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: transform 1.0s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.7s ease, margin-top 1.0s cubic-bezier(0.4, 0, 0.2, 1), padding 1.0s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 6;
  gap: $space-2;
  transform: translateY(10vh);
  text-decoration: none;
  color: inherit;
  padding: $space-3 $space-1;
  margin-top: 0;
  pointer-events: none; // Disattiva link se non in hover

  @include respond-below(md) {
    width: 100%;
    transform: translateY(0);
    gap: $space-0-5;
    padding: $space-1 0;
  }

  .is-hovered & {
    transform: translateY(0);
    padding: $space-4 $space-2 0 $space-2;
    pointer-events: auto; // Attiva link solo in hover

    @media (hover: hover) {
      cursor: pointer;

      &:hover {
        // Stesso ingrandimento del logo qui sotto: con 1.05 la scritta
        // cresceva meno del logo e stonava (segnalazione 24/07, 2026-09-16)
        .link-panel__title--hover {
          transform: scale(1.08);
          color: $clr-secondary;
          cursor: pointer;
        }
        .link-panel__logo {
          transform: scale(1.08);
        }
      }
    }

    @include respond-below(tablet-xl) {
      margin-top: 3rem;
      padding: $space-4 0 0 0;
    }

    @include respond-below(tablet) {
      margin-top: 4rem;
      padding: $space-4 0 0 0;
    }

    @include respond-below(tablet-sm) {
      margin-top: 5rem;
      padding: $space-4 0 0 0;
    }

    @include respond-below(md) {
      margin-top: 0;
      padding: $space-1 0;
    }
  }

  .is-dimmed & {
    opacity: 0.3;
    transform: translateY(-5vh);

    @include respond-below(md) {
       transform: none;
       opacity: 0.7;
    }
  }
}

.link-panel__logo {
  height: clamp(60px, 9vh, 90px);
  width: auto;
  margin-bottom: $space-0-5;
  transition: transform 0.7s ease, opacity 0.7s ease;
  opacity: 1;
  pointer-events: auto;

  @include respond-below(md) {
    height: 50px;
    margin-bottom: $space-0-25;

    .is-dimmed & {
      display: none;
    }
  }

  .is-dimmed & {
    opacity: 0;
    pointer-events: none;
  }
}

.link-panel__title {
  font-family: 'Karla', sans-serif;
  font-size: clamp(1.4rem, 3vw, 2.1rem);
  font-weight: 300;
  color: $text-color;
  text-align: center;
  margin-top: $space-1;
  margin-bottom: $space-0-5;
  width: fit-content;
  max-width: 550px;
  line-height: 1.2;
  transition: all 1.0s cubic-bezier(0.4, 0, 0.2, 1);

  // Ingrandimento e colore al passaggio del mouse con gli stessi tempi del logo
  // (transform 0.7s ease): prima erano 0.4s e 1.0s, e logo e scritta finivano
  // in momenti diversi. Il resto (apertura del pannello) resta a 1.0s.
  @media (hover: hover) {
    transition: all 1.0s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s ease, color 0.7s ease;
  }

  span {
    color: inherit;
    text-decoration: none;
    // Niente pointer-events:none qui: con NVDA il mouse sopra la scritta non leggeva
    // nulla (lo span veniva saltato). Il clic arriva comunque al link che lo contiene.
    // (2026-09-16, provato con NVDA 2026.2 su Edge)
  }

  @include respond-below(md) {
    width: 100%; // Torna a 100% su mobile per centratura flex
    font-size: clamp(1.1rem, 4.5vw, 1.4rem);
    margin-bottom: $space-0-25;
  }

  .is-hovered & {
    margin-bottom: 0;
  }
}

.link-panel__buttons {
  display: flex;
  gap: $space-2;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: $space-2;
  position: relative;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.7s ease;

  @include respond-below(md) {
    display: none;
    flex-direction: column;
    align-items: center;
    gap: $space-0-5; // Ridotto da $space-0-75
    width: 100%;
    margin-top: $space-0-5; // Ridotto da $space-1
  }

  .is-hovered & {
    opacity: 1;
    pointer-events: auto;

    @include respond-below(md) {
      display: flex;
    }
  }

  .is-dimmed & {
    opacity: 0;
    pointer-events: none;
  }
}

.bottom-action {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 240px;
  height: 55px;
  background: $clr-white;
  border: 1px solid $clr-border;
  box-shadow: 0 4px 20px rgba($clr-brand-dark, 0.12);
  text-decoration: none;
  transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease;
  box-sizing: border-box;
  padding: 0 $space-0-5;
  overflow: hidden;

  @include respond-below(md) {
    height: 50px;
    min-width: 220px;
    transform: none !important;
    box-shadow: 0 4px 20px rgba($clr-brand-dark, 0.12) !important;
  }

  &:focus-visible {
    transform: scale(1.02);
    box-shadow: 0 8px 32px rgba($clr-brand-dark, 0.2);
    border-color: rgba($clr-brand-dark, .35);
  }

  @media (hover: hover) {
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 8px 32px rgba($clr-brand-dark, 0.2);
      border-color: rgba($clr-brand-dark, .35);
    }
  }

  // Tre spazi uguali: bordo sinistro -> logo, logo -> contenitore delle due
  // scritte, contenitore -> bordo destro (richiesta di Teo, 2026-10-02; fino ad
  // allora la scritta era centrata nello spazio a destra del logo e i tre spazi
  // dipendevano dalla lunghezza del testo). Lo stesso valore fa da rientro e da
  // distanza; se il bottone resta alla larghezza minima, space-evenly divide
  // l'avanzo in tre parti uguali. Logo e scritte non portano rientri propri:
  // qualunque loro padding laterale romperebbe l'uguaglianza.
  &--banking {
    justify-content: space-evenly;
    padding: 0 1.5rem;
    column-gap: 1.5rem;
    min-width: 240px;

    .bottom-action__logo {
      flex: none;
      // altezza = misura visibile del logo prima del 2026-10-02; la larghezza
      // segue l'immagine, cosi' il riquadro coincide col disegno
      height: 2.35rem;
      max-width: none;
      padding: 0;
    }

    .bottom-action__text {
      flex: none;
      padding: 0;
    }

    @media (max-width: 1399px) and (min-width: 1200px) {
      min-width: 200px;
      height: 46px;
      padding: 0 1.2rem;
      column-gap: 1.2rem;

      .bottom-action__logo {
        height: 1.89rem;
      }
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      min-width: 160px;
      height: 37px;
      padding: 0 0.95rem;
      column-gap: 0.95rem;

      .bottom-action__logo {
        height: 1.52rem;
      }
    }

    @media (max-width: 999px) and (min-width: 800px) {
      min-width: 140px;
      height: 32px;
    }

    @include respond-below(md) {
      width: 260px;
      min-width: 260px;
      height: 44px;
    }
  }

  &--title {
    justify-content: center;
    padding: 0 $space-3;
    min-width: 450px;
    width: fit-content;
    max-width: 900px;

    .bottom-action__text {
      align-items: center;
      text-align: center;
    }

    .bottom-action__text-primary {
      text-transform: uppercase;
      font-size: 1.45rem;
      letter-spacing: 0.8px;
      white-space: nowrap;
      font-weight: 500;
    }

    @include respond-below(md) {
      min-width: 220px;
      max-width: 90%;
      height: auto;
      min-height: 40px;
      padding: $space-0-25 $space-1;
      justify-content: center; // Forza la centratura per il titolo

      .bottom-action__text {
        align-items: center;
        text-align: center;
        transform: scale(1); // Reset scala per leggibilità titolo
        flex: 0 1 auto;
      }

      .bottom-action__text-primary {
        font-size: 1rem;
        white-space: normal;
      }
    }
  }

  .vertical-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    pointer-events: none;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      background: $clr-border;
      height: 0;
      transition: height 0.7s ease, background 0.7s ease;
    }

    &::before { bottom: 50%; }
    &::after { top: 50%; }
  }

  @media (hover: hover) {
    &:hover .vertical-bar::before,
    &:hover .vertical-bar::after {
      height: 50%;
      background: $clr-brand-dark;
    }

    &--banking-left:hover .vertical-bar::before,
    &--banking-left:hover .vertical-bar::after {
      background: $clr-tertiary;
    }

    &--banking-right:hover .vertical-bar::before,
    &--banking-right:hover .vertical-bar::after {
      background: $clr-brand-dark;
    }
  }

  @include respond-below(md) {
    .vertical-bar::before,
    .vertical-bar::after {
      height: 50% !important;
    }

    &--banking-left .vertical-bar::before,
    &--banking-left .vertical-bar::after {
      background: $clr-brand-dark !important;
    }

    &--banking-right .vertical-bar::before,
    &--banking-right .vertical-bar::after {
      background: $clr-brand-dark !important;
    }
  }

  &--banking-left {
    @include respond-below(md) {
      .bottom-action__text-primary,
      .bottom-action__text-secondary {
        color: $clr-brand-dark !important;
      }
    }

    @media (hover: hover) {
      &:hover {
        .bottom-action__logo {
          filter: brightness(0) saturate(100%) invert(49%) sepia(11%) saturate(1915%) hue-rotate(52deg) brightness(92%) contrast(82%);
        }

        .bottom-action__text-primary,
        .bottom-action__text-secondary {
          color: $clr-tertiary !important;
        }
      }
    }
  }

  &--banking-right {
    @include respond-below(md) {
      .bottom-action__text-primary,
      .bottom-action__text-secondary {
        color: $clr-brand-dark !important;
      }
    }

    @media (hover: hover) {
      &:hover {
        .bottom-action__text-primary,
        .bottom-action__text-secondary {
          color: $clr-brand-dark !important;
        }
      }
    }
  }

  &__logo {
    height: 100%;
    width: auto;
    max-width: 68px;
    object-fit: contain;
    padding: 0.4rem;
    padding-left: 1.5rem;
    margin: 0;
    filter: brightness(0) saturate(100%) invert(18%) sepia(21%) saturate(1547%) hue-rotate(115deg) brightness(96%) contrast(92%);
    transition: filter 0.3s ease;

    @media (max-width: 1399px) and (min-width: 1200px) {
      max-width: 55px;
      padding: 0.35rem;
      padding-left: 1.2rem;
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      max-width: 44px;
      padding: 0.28rem;
      padding-left: 0.95rem;
    }

    @media (max-width: 999px) and (min-width: 800px) {
      max-width: 38px;
      padding: 0.25rem;
      padding-left: 0.85rem;
    }

    @include respond-below(md) {
      max-width: 60px;
      padding-left: 1rem;
    }
  }

  // Titolo e scritta sotto centrati fra loro (richiesta di Teo, 2026-09-17:
  // prima erano a sinistra e le due righe non partivano dallo stesso punto).
  // Nei bottoni banking larghezza e rientri li decide `&--banking` (2026-10-02).
  &__text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex: 1;
    padding: 0 0.6rem;

    @include respond-below(md) {
      align-items: flex-start;
      text-align: left;
      padding-left: 0.5rem;
      padding-right: 0;
    }
  }

  &__text-primary {
    font-family: 'Karla', sans-serif;
    font-size: 1.2rem;
    font-weight: 400;
    color: $clr-brand-gray;
    line-height: 1.2;
    opacity: 1;
    @media (max-width: 1399px) and (min-width: 1200px) {
      font-size: 0.95rem;
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      font-size: 0.75rem;
    }

    @media (max-width: 999px) and (min-width: 800px) {
      font-size: 0.65rem;
    }

    @include respond-below(md) {
      font-size: 1rem;
      font-weight: 600;
      padding-left: 0;
    }
  }

  @keyframes fadeInText {
    to {
      opacity: 1;
    }
  }

  &__text-secondary {
    font-family: 'Karla', sans-serif;
    font-size: 0.8rem;
    font-weight: 500;

    @media (max-width: 1399px) and (min-width: 1200px) {
      font-size: 0.64rem;
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      font-size: 0.50rem;
    }

    @media (max-width: 999px) and (min-width: 800px) {
      font-size: 0.44rem;
    }
    color: $clr-brand-gray;
    line-height: 1.2;
    margin-top: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.5s ease;

    @include respond-below(md) {
      opacity: 1;
      max-height: 50px;
      margin-top: 2px;
      padding-left: 0;
      font-size: 0.75rem;
    }

    @media (hover: hover) {
      .bottom-action--banking:hover & {
        max-height: 50px;
        margin-top: 2px;
        animation: fadeInText 0.5s ease 0.3s forwards;
      }
    }
  }
}

.bottom-action--placeholder {
  min-width: 240px;
  height: 50px;
  visibility: hidden;
}

.news-section-title {
  font-family: 'Karla', sans-serif;
  font-size: 1.1rem;
  font-weight: 500;
  text-transform: uppercase;
  color: $clr-brand-dark;
  margin: 0;
  padding-left: 2.8rem;
  text-align: left;
  letter-spacing: 0.5px;

  @media (max-width: 1399px) and (min-width: 1200px) {
    font-size: 0.92rem;
    margin-bottom: 0;
    padding-left: 2.8rem;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    font-size: 0.73rem;
    margin-bottom: 0;
    padding-left: 2.24rem;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    font-size: 0.64rem;
    margin-bottom: 0;
    padding-left: 1.96rem;
  }

  @include respond-below(md) {
    font-size: 0.95rem;
    margin-bottom: 0;
    padding-left: $space-1;
  }
}

/* NEWS INTEGRATE */
.news-integrated {
  width: 100%;
  max-width: 100%;
  padding: 0 $space-4;
  position: relative;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: all 1.0s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 0;
  transform: translateY(4vh);

  @include respond-below(tablet-xl) {
    transform: translateY(4vh);
  }

  @include respond-below(tablet) {
    transform: translateY(4vh);
  }

  @include respond-below(tablet-sm) {
    transform: translateY(4vh);
  }

  @include respond-below(md) {
    padding: 0 $space-0-5;
    transform: translateY(0);
    display: none;
    margin-top: -$space-1; // Ridotto da $space-1 (ora negativo per avvicinare ai bottoni)
  }

  &--left {
    padding-right: 15vw; // Sposta le card a sinistra per non essere coperte dall'arnia

    @include respond-below(md) {
      padding-right: $space-1;
    }
  }

  &--right {
    padding-left: 15vw; // Sposta le card a destra per non essere coperte dall'arnia

    @include respond-below(md) {
      padding-left: $space-1;
    }
  }

  .is-hovered & {
    opacity: 1;
    pointer-events: auto;

    @media (max-width: 1399px) and (min-width: 1200px) {
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
    }

    @media (max-width: 999px) and (min-width: 800px) {
    }

    @media (min-width: 1400px) {
      transform: translateY(4vh);
    }

    @include respond-below(md) {
      display: block; // Mostra solo in hover
    }
  }
}

.carousel-viewport {
  width: 100%;
  padding: $space-1-25 0;

  /* Col mouse il carosello NON e' scorrevole dall'utente: frecce, puntini e
     trascinamento spostano lo scrollLeft da codice, che funziona anche con
     overflow hidden. Un contenitore scorrevole con aggancio obbligatorio va
     riagganciato a ogni cambio di dimensione, cioe' a ogni fotogramma
     dell'apertura dei pannelli: era una delle due cause degli scatti
     (2026-09-17). Sul dito (iPad in orizzontale) resta lo scroll nativo, sotto. */
  overflow: hidden;
  overscroll-behavior-x: contain;

  @media (hover: none) {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
  }
  cursor: grab;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.is-dragging {
    scroll-snap-type: none;
    cursor: grabbing;
    user-select: none;
  }
}

.news-carousel-relative-wrapper {
  position: relative;
  width: 100%;
}

.carousel-track {
  display: flex;
  gap: $space-2;

  @media (max-width: 1399px) and (min-width: 1200px) {
    gap: 26px;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    gap: 21px;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    gap: 18px;
  }

  @include respond-below(md) {
    gap: $space-1;
  }
}

.news-card {
  scroll-snap-align: start;
  flex: 0 0 240px; // Ridotto da 280
  background: $clr-white;
  border: 1px solid $clr-border;
  border-radius: 0 !important; // Rimosso ogni arrotondamento
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);

  @media (max-width: 1399px) and (min-width: 1200px) {
    flex: 0 0 200px;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    flex: 0 0 160px;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    flex: 0 0 140px;
  }

  @include respond-below(md) {
    flex: 0 0 200px;
    scroll-snap-align: start;
    transform: none !important;
    box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05) !important;
    border-color: $clr-border !important;

    .news-title {
      transform: none !important;
      color: $text-color !important;
    }
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
      border-color: rgba($clr-brand-dark, 0.2);

      .news-title {
        color: $clr-brand-dark; // Verde Molto Scuro / Brand
        transform: scale(1.05);
      }
    }
  }

  .news-card-link {
    text-decoration: none;
    color: inherit;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .news-image-wrapper {
    height: 140px; // Ridotto da 180
    overflow: hidden;
    position: relative;
    border-radius: 0 !important;

    @media (max-width: 1399px) and (min-width: 1200px) {
      height: 117px;
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      height: 93px;
    }

    @media (max-width: 999px) and (min-width: 800px) {
      height: 82px;
    }

    @include respond-below(md) {
      height: 100px;
    }
  }

  .news-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
    border-radius: 0 !important;
  }

  .news-content {
    padding: $space-1-5;
    background: $clr-white;
    display: flex;
    flex-direction: column;
    flex: 1;

    @media (max-width: 1399px) and (min-width: 1200px) {
      padding: 1.25rem;
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      padding: 1.0rem;
    }

    @media (max-width: 999px) and (min-width: 800px) {
      padding: 0.8rem;
    }
  }

  .news-title {
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
    height: 2.8em;
    overflow: hidden;
    color: $text-color;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    transition: all 0.3s ease;

    @media (max-width: 1399px) and (min-width: 1200px) {
      font-size: 0.92rem;
    }

    @media (max-width: 1199px) and (min-width: 1000px) {
      font-size: 0.73rem;
    }

    @media (max-width: 999px) and (min-width: 800px) {
      font-size: 0.64rem;
    }

    @include respond-below(md) {
      font-size: 0.95rem;
    }
  }

  .news-date {
    font-size: 0.85rem;
    color: $clr-text-light;
    margin-top: 0.75rem;
    font-weight: 500;
  }
}

/* Dots Navigation */
.dots-navigation {
  display: none;

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba($clr-text-light, 0.3);
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.3s ease;

    @include respond-below(md) {
      width: 6px;
      height: 6px;
    }

    @media (hover: hover) {
      &:hover:not(.active) {
        background: rgba($clr-text-light, 0.5);
        transform: scale(1.2);
      }
    }

    &.active {
      background: $clr-tertiary; // Verde Istituzionale
      width: 24px;
      border-radius: 5px;

      @include respond-below(md) {
        width: 12px;
      }
    }
  }
}

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 70px;
  background: rgba($clr-white, 0.65);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.1);
  padding: 0;

  @include respond-below(md) {
    display: none; // Nascondiamo le frecce su mobile
  }

  svg {
    width: 24px;
    height: 24px;
    color: $clr-brand-dark;
    transition: color 0.3s ease;
  }

  @media (hover: hover) {
    &:hover {
      background: rgba($clr-white, 1);
      transform: translateY(-50%) scale(1.08);
      box-shadow: 0 6px 20px rgba($clr-brand-dark, 0.15);

      svg {
        color: $clr-tertiary; // Verde Istituzionale
      }
    }
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }

  &.arrow-left {
    left: -($space-2-5);

    .news-integrated--left & {
      left: -($space-2-5);
    }

    .news-integrated--right & {
      left: -3vw;
    }
  }

  &.arrow-right {
    right: -($space-2-5);

    .news-integrated--left & {
      right: -3vw;
    }

    .news-integrated--right & {
      right: -($space-2-5);
    }
  }
}

.separator {
  width: 2px;
  background: linear-gradient(to bottom, transparent, $clr-border 20%, $clr-border 80%, transparent);
  z-index: 15;

  @include respond-below(md) {
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, transparent, $clr-border 20%, $clr-border 80%, transparent);
  }
}

.center-image-wrapper {
  position: absolute;
  left: 50%;
  top: 45vh; // Abbassata da 32vh per centrarla meglio tra i nuovi blocchi
  transform: translate(-50%, -50%);
  z-index: 20;
  transition: all 1.0s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;

  &.at-left { left: 80%; }
  &.at-right { left: 20%; }

  @include respond-below(md) {
    top: 50%; // Allineata al separatore centrale (layout 50/50)
  }
}

.center-image {
  width: 400px;
  height: auto;
  transition: all 1.0s ease;

  @media (max-width: 1399px) and (min-width: 1200px) {
    width: 332px;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    width: 268px;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    width: 232px;
  }

  @include respond-below(md) {
    width: 60px; // Rimpicciolita di tantissimo
  }
}

.banner-fade-enter-active, .banner-fade-leave-active {
  transition: opacity 0.7s ease;
}
.banner-fade-enter-from, .banner-fade-leave-to {
  opacity: 0;
}

// ─── MOBILE INTRO ANIMATION ──────────────────────────────────────────────────
// Sequenza:
//   1) Arnia grande centrata + loading dots pulsanti (1.1s)
//   2) Arnia si rimpicciolisce verso il suo posto (1.4s, easing cinematografico)
//   3) Pannelli, loghi e titoli appaiono con slide-up staggerato (0.7s)
// ──────────────────────────────────────────────────────────────────────────────

@include respond-below(md) {

  // ── FASE 1: Stato iniziale — arnia grande, pannelli e contenuto nascosti ──
  .section-split-links-wrapper.mobile-intro-active {

    .link-panel,
    .separator {
      opacity: 0;
      transition: none !important;
    }

    .center-image-wrapper {
      top: 50% !important;
      transition: none !important;
    }

    .center-image {
      width: 82vw !important;
      transition: none !important;
    }

    // Loader visibile
    .mobile-loader {
      opacity: 1;
      transition: none !important;
    }
  }

  // ── FASE 2: Arnia si rimpicciolisce ──
  // Quando mobile-intro-active viene rimossa, partono le transizioni
  .section-split-links-wrapper:not(.mobile-intro-active) {

    // Arnia: scale-down fluido verso la posizione finale
    .center-image-wrapper {
      transition:
        top 1.4s cubic-bezier(0.22, 1, 0.36, 1),
        left 1.0s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    .center-image {
      transition: width 1.4s cubic-bezier(0.22, 1, 0.36, 1) !important;
    }

    // Loader: svanisce
    .mobile-loader {
      opacity: 0;
      transition: opacity 0.35s ease !important;
    }
  }

  // ── FASE 3: Pannelli e contenuto appaiono ──
  // Attivata da mobile-content-visible (aggiunta 600ms dopo la fine della fase 2)

  // Prima che i contenuti siano visibili, pannelli nascosti (ma non durante normale navigazione desktop)
  .section-split-links-wrapper:not(.mobile-content-visible) {
    .link-panel,
    .separator {
      opacity: 0;
    }
  }

  .section-split-links-wrapper.mobile-content-visible {

    // Pannelli: fade in
    .link-panel {
      opacity: 1;
      transition:
        opacity 0.5s ease,
        flex 1.0s cubic-bezier(0.4, 0, 0.2, 1),
        height 1.0s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    .separator {
      opacity: 1;
      transition: opacity 0.4s ease !important;
    }

    // Logo sinistra: slide up + fade
    .link-panel--left .link-panel__logo {
      animation: mobile-slide-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0s both;
    }

    // Titolo sinistra: stagger +100ms
    .link-panel--left .link-panel__title {
      animation: mobile-slide-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
    }

    // Logo destra: stagger +200ms
    .link-panel--right .link-panel__logo {
      animation: mobile-slide-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both;
    }

    // Titolo destra: stagger +300ms
    .link-panel--right .link-panel__title {
      animation: mobile-slide-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both;
    }
  }

  // ── KEYFRAME: Slide up + fade (loghi e titoli) ──
  @keyframes mobile-slide-up {
    from {
      opacity: 0;
      transform: translateY(28px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // ── MOBILE LOADER ──
  // Posizionato in basso nella schermata, lontano dall'arnia grande
  .mobile-loader {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 9%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    z-index: 35;
    opacity: 0; // nascosto di default — visibile solo in .mobile-intro-active
    pointer-events: none;

    // Label superiore
    &__label {
      font-family: 'Karla', sans-serif;
      font-size: 0.58rem;
      font-weight: 500;
      letter-spacing: 0.3em;
      text-transform: uppercase;
      color: rgba($clr-brand-dark, 0.38);
      animation: label-blink 1.6s ease-in-out infinite;
    }

    // Track della barra
    &__track {
      width: 110px;
      height: 1.5px;
      background: rgba($clr-brand-dark, 0.1);
      position: relative;
      overflow: hidden;
    }

    // Fill animato con gradient + glow
    &__fill {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 0;
      background: linear-gradient(to right, $clr-tertiary, $clr-brand-dark);
      box-shadow: 2px 0 6px rgba($clr-brand-dark, 0.5);
      animation: loader-fill 1.05s cubic-bezier(0.3, 0, 0.6, 1) forwards;
    }

  }

  @keyframes loader-fill {
    from { width: 0%; }
    to   { width: 100%; }
  }

  @keyframes label-blink {
    0%, 100% { opacity: 0.38; }
    50%       { opacity: 0.7;  }
  }
}

// ─── MOBILE HOMEPAGE LAYOUT ───────────────────────────────────────────────────
// Proporzioni derivate dal mockup: header ~10vh, grid 46vh, bottom ~44vh.
// Il grid ha altezza FISSA (flex: 0 0 46vh) — così 1fr funziona davvero
// e le immagini grandi non rompono il layout.
// ─────────────────────────────────────────────────────────────────────────────

.mobile-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: $clr-bg-page;
  gap: 1.5vh; // spazio visibile tra header, grid e bottom
}

// ── Loading: area news con spinner/grid ──
.mobile-news-area {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: $clr-bg-page;
  position: relative;
}

.mobile-spinner-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dots-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;

  .dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    animation: dot-pulse 0.9s ease-in-out infinite;

    &.dot-1 {
      background-color: #c9d745;
      animation-delay: 0s;
    }
    &.dot-2 {
      background-color: #8bbb58;
      animation-delay: 0.2s;
    }
    &.dot-3 {
      background-color: #629933;
      animation-delay: 0.4s;
    }
  }
}

@keyframes dot-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.7);
    opacity: 1;
  }
}

// Spinner fade out
.mobile-spinner-leave-active {
  transition: opacity 0.3s ease;
}
.mobile-spinner-leave-to {
  opacity: 0;
}

// News reveal: slide up + fade in
// showNews entra nel DOM a +350ms da isLoading=false
// bottoni finiscono a +350ms (loro delay) + 650ms (durata) = +1000ms da isLoading=false
// quindi dal momento in cui showNews=true, i bottoni finiscono a 650ms dopo
.mobile-news-enter-active {
  transition: opacity 0.5s ease 0.65s;
}
.mobile-news-enter-from {
  opacity: 0;
}

// ── Header: logo prominente, centrato, con respiro verticale ──
.mobile-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5.5vh 1.5rem 1.5vh;
  background: $clr-bg-page;
}

.mobile-header__logo {
  height: clamp(48px, 8vh, 70px);
  width: auto;
}

// ── Grid 2×2 — si espande per riempire il blocco superiore ──
.mobile-news-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 0.75rem;
  padding: 0.75rem 2.5rem 1rem;  // doppio padding laterale
  overflow: hidden;
  background: $clr-bg-page;
  max-width: 500px;   // cap larghezza a tablet — mantiene proporzioni mobile
  width: 100%;
  margin: 0 auto;
}

// Ogni card con bordo sottile, senza angoli arrotondati
.mobile-news-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  color: $text-color;
  background: $clr-white;
  border: 1px solid $clr-border;
  border-radius: 0;
  box-shadow: 0 1px 4px rgba($clr-brand-dark, 0.07);
  max-height: 210px;  // mantiene proporzioni mobile anche a tablet

  &__img-wrap {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: $clr-bg-card;
  }

  &__img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__body {
    flex-shrink: 0;
    padding: 0.4rem 0.5rem 0.5rem;
  }

  &__title {
    font-family: 'Karla', sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.25;
    margin: 0;
    color: $text-color;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

// ── Bottom: immagine castello + 2 bottoni in flow normale ──
// Struttura flex-column:
//   1. __castle  → flex: 1, occupa tutto lo spazio sopra i bottoni
//                   object-position: bottom → la base dell'immagine tocca il primo bottone
//   2. __cta-group → flex: 0 0 auto, bottoni alti e fissi sotto l'immagine
//
// Altezza totale fissa (44vh) perché il parent è 100vh.
// ──────────────────────────────────────────────────────────────────────
$mobile-cta-diagonal: 10px;

.mobile-bottom {
  flex: 0 0 auto;           // si dimensiona sui figli (tutti con altezze fisse)
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // Castello: altezza naturale dell'immagine, full width, no zoom
  &__castle {
    display: block;
    width: 100%;
    height: auto;
    // L'immagine portrait e' un SVG con solo viewBox, senza width/height:
    // Safari/iOS non ne deduce le proporzioni, applica preserveAspectRatio e
    // lascia bande vuote ai lati + uno stacco dal verde sotto. Dichiarare qui il
    // rapporto del viewBox (859.58 x 109.67) e riempire il box lo elimina.
    aspect-ratio: 859.58 / 109.67;
    object-fit: fill;
    background: linear-gradient(to bottom, transparent 70%, #3E7A22 100%);
  }

  // Gruppo bottoni: in flow, subito sotto il castello
  &__cta-group {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
  }

  // Bottone: altezza 14.58vh (16.2vh -10%)
  &__cta {
    height: 14.58vh;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-family: 'Karla', sans-serif;
    font-size: clamp(1.6rem, 7vw, 2.4rem);
    font-weight: 300;
    color: $clr-white;
    text-decoration: none;
    text-align: center;
    letter-spacing: 0.02em;
    position: relative;
    transition: height 0.65s cubic-bezier(0.4, 0, 0.2, 1) 0.35s;

    &--loading {
      height: 4vh;
      pointer-events: none;
    }

    &-label {
      transition: opacity 0.3s ease 1s;

      .mobile-bottom__cta--loading & {
        opacity: 0;
        transition: none;
      }
    }

    &--cassa {
      background: #3E7A22;
      z-index: 1;
    }

    // Fondazione: sovrapposto al cassa di $diagonal px, diagonale piena
    &--fondazione {
      background: #1C463A;
      z-index: 2;
      height: calc(14.58vh + #{$mobile-cta-diagonal});
      margin-top: -$mobile-cta-diagonal;
      clip-path: polygon(0 $mobile-cta-diagonal, 100% 0, 100% 100%, 0 100%);

      &.mobile-bottom__cta--loading {
        height: calc(4vh + #{$mobile-cta-diagonal});
      }
    }

    &:active { opacity: 0.85; }
  }

  &__footer-strip {
    height: 4vh;
    width: 100%;
    background: #122e25;
    position: relative;
    z-index: 3;
    margin-top: -$mobile-cta-diagonal;
    clip-path: polygon(0 $mobile-cta-diagonal, 100% 0, 100% 100%, 0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14vw;           // distanza tra i due, simmetrici rispetto al centro
    padding-top: 1.1vh;  // leggermente più in basso
  }

  &__footer-link {
    font-family: 'Karla', sans-serif;
    font-size: 0.62rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #122e25; // stesso verde della striscia: invisibili alla vista, ma restano cliccabili
    text-decoration: none;
    transition: color 0.3s ease, transform 0.3s ease;

    &:hover,
    &:active {
      color: #122e25; // restano nascosti anche al tap
    }
  }
}

// ── Landscape mobile: adattamento per orientamento orizzontale ──
@media (orientation: landscape) and (max-width: 1024px) {
  // Distribuisce lo spazio tra header, news e castello in modo proporzionale
  .mobile-layout {
    gap: 0;
    justify-content: space-between;
  }

  // Logo con respiro in alto
  .mobile-header {
    padding: 2vh 1.5rem 0;
  }

  .mobile-header__logo {
    height: 40px;
  }

  // news-area si riduce al contenuto: niente spazio vuoto sotto le cards
  .mobile-news-area {
    flex: 0 0 auto;
    overflow: hidden;
  }

  // News su una riga orizzontale (4 cards affiancate), altezza dal contenuto
  .mobile-news-grid {
    height: auto;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: auto;
    padding: 0 1.5rem;
    max-width: 100%;
    max-height: none;
  }

  .mobile-news-card {
    max-height: 120px;
  }

  // Castello: full width, proporzioni naturali
  // In landscape la sorgente e' un PNG (proporzioni diverse dall'SVG portrait e
  // dimensioni intrinseche gia' presenti): si torna al calcolo automatico.
  .mobile-bottom__castle {
    width: 100%;
    height: auto;
    max-height: none;
    aspect-ratio: auto;
    object-fit: contain;
  }

  // Bottoni: font ridotto, altezza lasciata al vh (già più piccolo in landscape)
  .mobile-bottom__cta {
    height: 13vh;
    font-size: clamp(1rem, 3vw, 1.5rem);

    &--loading {
      height: 4vh;
    }
  }

  .mobile-bottom__cta--fondazione {
    height: calc(13vh + #{$mobile-cta-diagonal});

    &.mobile-bottom__cta--loading {
      height: calc(4vh + #{$mobile-cta-diagonal});
    }
  }

  .mobile-bottom__footer-strip {
    height: 3.5vh;
  }
}
</style>
